import { useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";

import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import { LongArrowBackwardIcon } from "@jti/brand-icons/long-arrow-backward";
import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { BREAKPOINT_MEDIUM, BREAKPOINT_SMALL_LARGE } from "@jti/fe-common";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { ComprehensiveProductCard } from "@product-details/components/ComprehensiveProductCard";
import { ReactQueryClientProvider } from "@product-details/providers/ReactQueryClientProvider";

import "swiper/css";

import type { Teaser } from "./Teasers.type";

import styles from "./TeaserCarousel.module.scss";

export type TeaserCarouselProps = {
  title: string;
  carouselRedirectLabel?: string;
  carouselRedirectHref?: string;
  teasers: Teaser[];
};

export const TeaserCarousel: React.FC<TeaserCarouselProps> = ({
  title,
  carouselRedirectLabel,
  carouselRedirectHref,
  teasers = [],
}) => {
  const [prevEl, setPrevEl] = useState<HTMLElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLElement | null>(null);
  const { __ } = useTranslations();

  const isSmallLargeViewport = useMediaQuery({ maxWidth: BREAKPOINT_SMALL_LARGE });
  const isMediumViewport = useMediaQuery({ maxWidth: BREAKPOINT_MEDIUM });

  const visibleCount = useMemo(() => {
    if (isSmallLargeViewport) {
      return 1;
    } else if (isMediumViewport) {
      return 2;
    } else {
      return 3;
    }
  }, [isSmallLargeViewport, isMediumViewport]);

  const areButtonsVisible = teasers.length > visibleCount;

  const elements = teasers.map((teaser) => (
    <ComprehensiveProductCard
      {...teaser}
      key={teaser.productData.sku}
      hideLeftBadge={teaser.hideCustomBadge}
      leftBadge={teaser.customBadge}
      leftBadgeIcon={teaser.customBadgeIcon}
      leftBadgeDark={teaser.customBadgeDark}
      hidePriceInformation={teaser.hidePriceInformation}
      hidePrice={teaser.hidePrice}
      hideRightBadge={teaser.hideRightBadge}
      rightBadgeDark={teaser.rightBadgeDark}
      hideStrikethroughPrice={teaser.hideStrikethroughPrice}
      hideDiscountBadge={teaser.hideDiscountBadge}
      hideBuyCTA={teaser.hideBuyCTA}
      freeTrialProduct={false}
      hideDiscoverButton={teaser.hideDiscoverButton}
      hideDescription={teaser.hideDescription}
      productPageLink={teaser.productPageLink}
      sticksData={teaser.sticksData}
      productData={teaser.productData}
    />
  ));

  return (
    <ReactQueryClientProvider>
      <div>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          {carouselRedirectLabel && carouselRedirectHref && (
            <Button
              variant="text-muted"
              size="sm"
              href={carouselRedirectHref}
              className={styles.redirectButton}
              iconAfter={LongArrowForwardIcon}
            >
              {carouselRedirectLabel}
            </Button>
          )}
        </div>

        <Swiper
          slidesPerView={visibleCount}
          pagination={{
            clickable: true,
          }}
          modules={[Pagination, Navigation]}
          navigation={
            areButtonsVisible
              ? {
                  nextEl,
                  prevEl,
                }
              : false
          }
          className={styles.slides}
        >
          {elements.map((element) => (
            <SwiperSlide key={element.key} className={styles.swiperSlide}>
              <div className={styles.slide}>{element}</div>
            </SwiperSlide>
          ))}
        </Swiper>

        {areButtonsVisible && (
          <div className={styles.arrowButtons}>
            <button
              ref={setPrevEl}
              className={styles.arrowButton}
              data-testid="arrow-button-left"
              aria-label={__("carousel.previous")}
            >
              <LongArrowBackwardIcon width="32px" height="32px" aria-hidden="true" />
            </button>
            <button
              ref={setNextEl}
              className={styles.arrowButton}
              data-testid="arrow-button-right"
              aria-label={__("carousel.next")}
            >
              <LongArrowForwardIcon width="32px" height="32px" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </ReactQueryClientProvider>
  );
};
